import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { title: "", kind: "workshop", date_text: "", location: "Lime Studios, Pune", description: "", seats: "", fee: "", image: "", published: true };

const inputCls =
  "w-full bg-white border border-black/20 focus:border-lima-blue outline-none text-lima-ink text-sm px-3 py-2.5 rounded-none placeholder:text-black/30 transition-colors duration-200";
const labelCls = "block font-mono2 text-[11px] tracking-[0.25em] text-black/50 mb-1.5";

export default function EventForm({ open, onOpenChange, event, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(event ? { ...EMPTY, ...event } : { ...EMPTY });
  }, [open, event]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { title: form.title, kind: form.kind, date_text: form.date_text, location: form.location, description: form.description, seats: form.seats, fee: form.fee || "", image: form.image || "", published: form.published };
    try {
      if (event?.id) {
        await api.put(`/admin/events/${event.id}`, payload);
        toast.success("Event updated.");
      } else {
        await api.post("/admin/events", payload);
        toast.success("Event created.");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-none border-black/20" data-testid="event-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-2xl tracking-tight">
            {event?.id ? "Edit Event" : "New Event / Workshop"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-2">
          <div>
            <label className={labelCls}>TITLE *</label>
            <input required className={inputCls} data-testid="event-form-title" value={form.title} onChange={set("title")} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>TYPE</label>
              <select className={inputCls} data-testid="event-form-kind" value={form.kind} onChange={set("kind")}>
                <option value="OPEN DAY">Open Day</option>
                <option value="WORKSHOP">Workshop</option>
                <option value="LIVE SESSION">Live Session</option>
                <option value="MASTERCLASS">Masterclass</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>DATE</label>
              <input className={inputCls} data-testid="event-form-date" placeholder="SAT · 20 SEP 2026" value={form.date_text} onChange={set("date_text")} />
            </div>
            <div>
              <label className={labelCls}>LOCATION</label>
              <input className={inputCls} data-testid="event-form-location" value={form.location} onChange={set("location")} />
            </div>
            <div>
              <label className={labelCls}>SEATS</label>
              <input className={inputCls} data-testid="event-form-seats" placeholder="15 SEATS" value={form.seats} onChange={set("seats")} />
            </div>
            <div>
              <label className={labelCls}>FEE</label>
              <input className={inputCls} data-testid="event-form-fee" placeholder="e.g. Free or ₹999" value={form.fee} onChange={set("fee")} />
            </div>
          </div>
          <div>
            <label className={labelCls}>IMAGE URL</label>
            <input className={inputCls} data-testid="event-form-image" placeholder="https://…" value={form.image} onChange={set("image")} />
          </div>
          <div>
            <label className={labelCls}>DESCRIPTION</label>
            <textarea rows={3} className={`${inputCls} resize-none`} data-testid="event-form-description" value={form.description} onChange={set("description")} />
          </div>
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input type="checkbox" checked={form.published} data-testid="event-form-published"
              onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
              className="w-4 h-4 accent-[#0000EE]" />
            <span className="text-sm font-medium">Published (visible on the website)</span>
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} data-testid="event-form-cancel"
              className="border border-black/20 px-6 py-3 text-sm font-semibold hover:bg-black/5 rounded-none flex items-center gap-2">
              <X size={14} /> Cancel
            </button>
            <button type="submit" disabled={saving} data-testid="event-form-save"
              className="bg-lima-blue hover:bg-lima-bluehover disabled:opacity-50 text-white px-8 py-3 text-sm font-semibold rounded-none transition-colors duration-200">
              {saving ? "SAVING…" : event?.id ? "Save Changes" : "Create Event"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
